import React, {
  useState,
  useContext,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useEffect,
} from "react";
import { Button, InputAdornment } from "@mui/material";
import { AddCircleOutline, Percent } from "@mui/icons-material";
import toast from "react-hot-toast";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import SessionsApi from "Classes/API/Administration/SessionsApi";
import searchSessionsReq from "Classes/Models/Requests/Administration/CreateSessionReq";
import { LanguageContext, UserContext } from "App";
import SessionRow from "./SessionRow";
import { daysOfWeek } from "layouts/Services/constants";

const SecondStage = forwardRef(({ patientId, contractId }, ref) => {
  const { translations } = useContext(LanguageContext);
  const User = useContext(UserContext);

  const [allSessionOptions, setAllSessionOptions] = useState([]);
  const [selectedSessions, setSelectedSessions] = useState([
    {
      sessionId: null,
      sessionName: "",
      quantity: 1,
      frequency: "Daily",
      sessionValue: null,
      selectedDays: [],
    },
  ]);
  const [discountPercent, setDiscountPercent] = useState("");

  useEffect(() => {
    const fetchInitialSessions = async () => {
      const searchReq = new searchSessionsReq();
      searchReq.pagingParams = { PageNumber: 1, PageSize: 100000 };

      if (contractId != null) {
        searchReq.ContractId = contractId;
      } else {
        searchReq.ContractId = undefined;
        searchReq.IsContractSession = false;
      }
      searchReq.Name = "";
      const res = await SessionsApi.SearchSessions(searchReq);
      if (res.status === "Success") {
        const formatted = res.data.list.map((item) => ({
          value: item,
          label: item.name,
        }));
        setAllSessionOptions(formatted);
      }
    };
    fetchInitialSessions();
  }, [contractId]);

  const handleUpdate = useCallback((index, field, value) => {
    setSelectedSessions((prev) => {
      const updated = [...prev];
      if (field === "sessionValue") {
        updated[index] = {
          ...updated[index],
          sessionValue: value,
          sessionId: value?.value.id,
          sessionName: value?.label,
        };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return updated;
    });
  }, []);

  const handleDiscountChange = (e) => {
    let v = e.target.value.replace(/[^0-9.]/g, "");
    if (v === "") {
      setDiscountPercent("");
      return;
    }
    let num = parseFloat(v);
    if (isNaN(num)) return;
    if (num > 100) num = 100;
    if (num < 0) num = 0;
    setDiscountPercent(num.toString());
  };

  const handleAddSession = () => {
    setSelectedSessions([
      ...selectedSessions,
      {
        quantity: 1,
        frequency: "Daily",
        sessionName: "",
        sessionId: null,
        selectedDays: [],
      },
    ]);
  };

  useImperativeHandle(ref, () => ({
    submit: async () => {
      const validSessions = selectedSessions.filter((s) => s.sessionName);
      if (validSessions.length === 0) {
        toast.error("Please select at least one session.");
        return false;
      }

      const invalidCustomSessions = validSessions.filter(
        (s) => s.frequency === "Custom" && s.selectedDays.length === 0,
      );
      if (invalidCustomSessions.length > 0) {
        toast.error("Please select at least one day for custom sessions.");
        return false;
      }

      try {
        const payload = {
          patientId,
          createdById: User.id,
          discountPercent:
            discountPercent === "" ? 0 : parseFloat(discountPercent),
          sessions: validSessions.map((s) => ({
            sessionName: s.sessionName,
            quantity: s.quantity,
            frequency: s.frequency,
            selectedDays: s.frequency === "Custom" ? s.selectedDays : [],
          })),
        };

        const res = await SessionsApi.CreatePatientSheet(payload);
        if (res.status === "Success") return true;
        throw new Error(res.message || "Failed to save");
      } catch (error) {
        toast.error("Error: " + error.message);
        return false;
      }
    },
  }));

  const getSessions = (inputValue, callback) => {
    if (!inputValue || inputValue.trim() === "") {
      callback(allSessionOptions);
      return;
    }
    const filtered = allSessionOptions.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
    callback(filtered);
  };

  return (
    <MDBox display="flex" flexDirection="column" gap={4}>
      <MDBox>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <MDTypography variant="h5" fontWeight="bold">
            Sessions Details
          </MDTypography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddCircleOutline />}
            onClick={handleAddSession}
            sx={{
              borderColor: (theme) => theme.palette.info.main,
              color: (theme) => theme.palette.info.main,
              "&:hover": {
                borderColor: (theme) => theme.palette.info.dark,
                backgroundColor: "rgba(3, 169, 244, 0.04)",
              },
              fontWeight: "bold",
              textTransform: "none",
              px: 3,
            }}
          >
            Add New Session
          </Button>
        </MDBox>

        <MDBox display="flex" flexDirection="column" gap={2}>
          {selectedSessions.map((session, index) => (
            <SessionRow
              key={index}
              index={index}
              session={session}
              isRemovable={selectedSessions.length > 1}
              onUpdate={handleUpdate}
              onRemove={(i) =>
                setSelectedSessions(
                  selectedSessions.filter((_, idx) => idx !== i),
                )
              }
              loadOptions={getSessions}
              allSessionOptions={allSessionOptions}
              translations={translations}
              daysOfWeek={daysOfWeek}
            />
          ))}
        </MDBox>
      </MDBox>

      <MDBox
        display="flex"
        flexDirection="column"
        gap={3}
        pt={3}
        sx={{
          borderTop: ({ borders: { borderWidth, borderColor } }) =>
            `${borderWidth[1]} solid ${borderColor}`,
        }}
      >
        <MDBox width="100%">
          <MDInput
            label="Applicable Discount"
            type="number"
            value={discountPercent}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Percent fontSize="small" color="info" />
                </InputAdornment>
              ),
            }}
            onChange={handleDiscountChange}
          />
        </MDBox>
      </MDBox>
    </MDBox>
  );
});

export default SecondStage;
